import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import {
  TextInput, SelectInput, CheckboxInput, FormRow
} from '@/components/common/FormField'
import { userService, departmentService } from '@/services/api'
import toast from 'react-hot-toast'
import { Shield } from 'lucide-react'

const ROLE_LABELS = {
  super_admin: 'Супер Администратор',
  admin: 'Администратор',
  department_admin: 'Администратор кафедры',
  hr: 'HR',
  viewer: 'Просмотр',
  operator: 'Оператор СКУД',
}

const INITIAL = {
  full_name: '',
  username: '',
  password: '',
  email: '',
  phone: '',
  role: '',
  department: '',
  is_active: true,
}

export default function UserModal({ open, onClose, user = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!user

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => userService.roles().then((r) => r.data.results || r.data),
    enabled: open,
  })

  const { data: depts } = useQuery({
    queryKey: ['all-departments'],
    queryFn: () => departmentService.list({ page_size: 200 }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      if (user) {
        setForm({
          full_name: user.full_name || '',
          username: user.username || '',
          password: '',
          email: user.email || '',
          phone: user.phone || '',
          role: user.role || '',
          department: user.department || '',
          is_active: user.is_active ?? true,
        })
      } else {
        setForm(INITIAL)
      }
      setErrors({})
    }
  }, [open, user])

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: val }))
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.full_name.trim()) e.full_name = 'Обязательное поле'
    if (!form.username.trim()) e.username = 'Обязательное поле'
    if (!isEdit && !form.password.trim()) e.password = 'Пароль обязателен при создании'
    if (form.password && form.password.length < 8) e.password = 'Минимум 8 символов'
    if (!form.role) e.role = 'Выберите роль'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)

    const payload = { ...form }
    if (!payload.password) delete payload.password
    if (!payload.department) delete payload.department

    try {
      if (isEdit) {
        await userService.update(user.id, payload)
        toast.success('Пользователь обновлён')
      } else {
        await userService.create(payload)
        toast.success('Пользователь создан')
      }
      qc.invalidateQueries(['system-users'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data) {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v[0] : v })
        setErrors(fieldErrors)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const roleOpts = (roles || []).map((r) => ({
    value: r.id,
    label: ROLE_LABELS[r.name] || r.name,
  }))
  const deptOpts = (depts || []).map((d) => ({ value: d.id, label: d.name }))

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Shield size={18} className="text-primary-600" />
          {isEdit ? 'Редактировать пользователя' : 'Добавить пользователя'}
        </span>
      }
      onSubmit={handleSubmit}
      loading={loading}
    >
      <TextInput
        label="ФИО" required
        value={form.full_name} onChange={set('full_name')}
        error={errors.full_name} placeholder="Иванов Иван Иванович"
      />

      <FormRow>
        <TextInput
          label="Логин" required
          value={form.username} onChange={set('username')}
          error={errors.username} placeholder="ivanov.i"
          hint="Только латинские буквы и цифры"
        />
        <TextInput
          label={isEdit ? 'Новый пароль (не менять — пусто)' : 'Пароль *'}
          type="password"
          value={form.password} onChange={set('password')}
          error={errors.password}
        />
      </FormRow>

      <FormRow>
        <TextInput
          label="Email"
          type="email" value={form.email} onChange={set('email')}
          placeholder="user@example.com"
        />
        <TextInput
          label="Телефон"
          type="tel" value={form.phone} onChange={set('phone')}
          placeholder="+998 90 000 00 00"
        />
      </FormRow>

      <SelectInput
        label="Роль" required
        value={form.role} onChange={set('role')}
        options={roleOpts} placeholder="Выберите роль"
        error={errors.role}
      />

      <SelectInput
        label="Кафедра / Отдел (необязательно)"
        value={form.department} onChange={set('department')}
        options={deptOpts} placeholder="Без привязки"
      />

      <CheckboxInput
        label="Пользователь активен"
        checked={form.is_active}
        onChange={set('is_active')}
      />
    </ModalForm>
  )
}
